import Image from 'next/image';

import { Box, Heading, LinkOverlay, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { PromoCard } from '~/features/experiments/ab_plp_redesign_2025/components/promo-card';
import { FlashSaleEyebrow } from '~/features/promotions/components/flash-sale-eyebrow';
import {
  DIWALI_2026_ASSETS,
  DIWALI_2026_COLORS,
  DIWALI_2026_COPY,
  DIWALI_2026_CTA_LABEL,
  DIWALI_2026_EYEBROW_LABEL,
} from '~/features/promotions/constants/diwali-2026';

export function Diwali2026HomepageCard({
  checkoutHref,
  imagePriority,
  onClick,
}: {
  checkoutHref: string;
  imagePriority?: boolean;
  onClick: () => void;
}) {
  const id = 'diwali-2026-title';

  return (
    <PromoCard
      backgroundColor='black'
      backgroundImageSlot={<HomepageBackground imagePriority={imagePriority} />}
      id={id}
      padding={{ base: '32px', md: '48px' }}
      sectionGap={{ base: 4, md: 6 }}
      buttonSlot={
        <ButtonLink
          linkProps={{ href: checkoutHref }}
          buttonProps={{
            _active: { backgroundColor: DIWALI_2026_COLORS.cta, opacity: 0.9 },
            _hover: { backgroundColor: DIWALI_2026_COLORS.cta, opacity: 0.9 },
            backgroundColor: DIWALI_2026_COLORS.cta,
            borderRadius: '4px',
            color: 'white',
            fontSize: '15px',
            fontWeight: '500',
            height: '45px',
            paddingX: '24px',
            position: 'relative',
            width: { base: '100%', md: '152px' },
            zIndex: 1,
            onClick,
          }}
        >
          {DIWALI_2026_CTA_LABEL}
        </ButtonLink>
      }
      contentSlot={
        <Box>
          <Heading
            as='h2'
            color='white'
            fontSize={{ base: '1.75rem', md: '2.25rem' }}
            fontWeight='600'
            id={id}
            letterSpacing='-0.01em'
            lineHeight={{ base: 1.15, md: 1.1 }}
            marginBottom={{ base: 4, md: 3 }}
          >
            <LinkOverlay href={checkoutHref} onClick={onClick}>
              <Box as='span' display={{ base: 'block', md: 'inline' }}>
                {DIWALI_2026_COPY.titleLine1}
              </Box>{' '}
              {DIWALI_2026_COPY.titleLine2}
            </LinkOverlay>
          </Heading>
          <Text
            color={DIWALI_2026_COLORS.accent}
            fontSize={{ base: '0.875rem', md: 'md' }}
            letterSpacing={{ base: '-0.01em', md: 'normal' }}
            lineHeight={{ base: 1.4, md: 1.5 }}
            maxWidth={{ base: '186px', md: 'calc(100% - 240px)', xl: 'none' }}
          >
            {DIWALI_2026_COPY.homepageSubtitle}
          </Text>
        </Box>
      }
      eyebrowSlot={
        // The divider must stop short of the diyas, which sit a fixed distance from the right edge
        // because the background scales with card height.
        <Box maxWidth={{ md: 'min(781px, calc(100% - 240px))' }} width='100%'>
          <FlashSaleEyebrow
            color={DIWALI_2026_COLORS.accent}
            dividerGradient={DIWALI_2026_COLORS.dividerGradient}
            icon='clock'
            label={DIWALI_2026_EYEBROW_LABEL}
          />
        </Box>
      }
    />
  );
}

function HomepageBackground({ imagePriority }: { imagePriority?: boolean }) {
  return (
    <Box bottom={0} left={0} pointerEvents='none' position='absolute' right={0} top={0} zIndex={0}>
      <Box display={{ base: 'block', md: 'none' }} height='100%' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='100vw'
          src={DIWALI_2026_ASSETS.homepageBgMobile}
          style={{ objectFit: 'cover', objectPosition: 'right top' }}
          fill
        />
      </Box>
      <Box display={{ base: 'none', md: 'block' }} height='100%' position='relative' width='100%'>
        <Image
          alt=''
          loading='eager'
          priority={imagePriority}
          sizes='(max-width: 1120px) 100vw, 1056px'
          src={DIWALI_2026_ASSETS.homepageBg}
          style={{ objectFit: 'cover', objectPosition: 'right center' }}
          fill
        />
      </Box>
    </Box>
  );
}
